import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isDescendant, pathTo, type TreeNode } from '../tree-model';
import {
  BoardIcon,
  ChartIcon,
  FormatIcon,
  Chevron,
  DocIcon,
  FolderIcon,
  GovernanceIcon,
  LibraryIcon,
  LockIcon,
  PersonIcon,
  KanbanIcon,
  LearningIcon,
  MailIcon,
  BrowserIcon,
  PlanIcon,
  ReportIcon,
  SheetIcon,
  WorkflowIcon,
  ChatIcon,
  CloudIcon,
  ConnectedFolderIcon,
  DashboardIcon,
} from './icons';

export interface TreeCommands {
  canManage: boolean;
  canPublish: boolean;
  newFolder: (parentFolderId: string | null) => void;
  newSheet: (folderId: string | null, isPrivate: boolean) => void;
  rename: (node: TreeNode, name: string) => Promise<void>;
  move: (node: TreeNode) => void;
  /** Drop `dragged` into folder `targetFolderId` (null = project root). */
  drop: (dragged: TreeNode, targetFolderId: string | null) => void;
  publish: (node: TreeNode, folderId?: string | null) => void;
  canManageBoards: boolean;
  newBoard: () => void;
  /** §23 M12.7 the project's own brand (owners / PMs; the organisation's is in Administration). */
  canBrand: boolean;
  openBrand: () => void;
  /** Data lifecycle: organisation admins archive the project (or restore it / change its retention). */
  archived: boolean;
  canArchive: boolean;
  archive: (mode: 'archive' | 'unarchive' | 'retention') => void;
}

interface Props {
  nodes: TreeNode[];
  selectedKey: string | null;
  storageKey: string;
  commands: TreeCommands;
}

const ICON: Record<TreeNode['kind'], (open: boolean) => JSX.Element> = {
  workspace: () => <PersonIcon />,
  myDashboard: () => <DashboardIcon />,
  root: () => <LibraryIcon />,
  folder: (open) => <FolderIcon open={open} />,
  sheet: () => <SheetIcon />,
  doc: () => <DocIcon />,
  plans: () => <PlanIcon />,
  plan: () => <PlanIcon />,
  governance: () => <GovernanceIcon />,
  board: () => <BoardIcon />,
  workflows: () => <WorkflowIcon />,
  reporting: () => <ChartIcon />,
  execDashboard: () => <ChartIcon />,
  dashboard: () => <ChartIcon />,
  report: () => <ReportIcon />,
  learning: () => <LearningIcon />,
  mail: () => <MailIcon />,
  browser: () => <BrowserIcon />,
  actions: () => <KanbanIcon />,
  admin: () => <PersonIcon />,
  people: () => <PersonIcon />,
  chat: () => <ChatIcon />,
  connected: () => <ConnectedFolderIcon />,
};

const isContainer = (n: TreeNode) =>
  ['folder', 'root', 'workspace', 'plans', 'governance', 'reporting', 'admin'].includes(n.kind) ||
  // §27 M13.1(c) a plan that others roll up to holds them
  (n.kind === 'plan' && n.children.length > 0);
const isDropTarget = (n: TreeNode) => n.kind === 'folder' || n.kind === 'root';
const folderIdOf = (n: TreeNode) => (n.kind === 'folder' ? n.id : null);

/** Windows-Explorer-style tree: chevrons, icons, keyboard nav (WAI-ARIA tree), F2 rename, context menu, drag-and-drop. */
export function ProjectTree({ nodes, selectedKey, storageKey, commands }: Props) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) ?? 'null') as string[] | null;
      if (saved) return new Set(saved);
    } catch {
      /* per-viewer convenience only */
    }
    return new Set(['workspace', 'root', 'plans', 'governance', 'reporting', 'admin']);
  });
  const [focusKey, setFocusKey] = useState<string | null>(selectedKey);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ node: TreeNode; x: number; y: number } | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);
  const treeRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify([...expanded]));
    } catch {
      /* ignore */
    }
  }, [expanded, storageKey]);

  // Reveal the selected item (like Explorer's "expand to current folder").
  useEffect(() => {
    if (!selectedKey) return;
    const path = pathTo(nodes, selectedKey);
    if (path) setExpanded((e) => new Set([...e, ...path.slice(0, -1).map((n) => n.key)]));
    setFocusKey(selectedKey);
  }, [selectedKey, nodes]);

  const visible = useMemo(() => {
    const out: { node: TreeNode; depth: number; parent: TreeNode | null }[] = [];
    const walk = (list: TreeNode[], depth: number, parent: TreeNode | null) => {
      for (const n of list) {
        out.push({ node: n, depth, parent });
        if (expanded.has(n.key)) walk(n.children, depth + 1, n);
      }
    };
    walk(nodes, 0, null);
    return out;
  }, [nodes, expanded]);

  const toggle = (key: string, open?: boolean) =>
    setExpanded((e) => {
      const next = new Set(e);
      if (open ?? !next.has(key)) next.add(key);
      else next.delete(key);
      return next;
    });

  // Opening a folder also expands it (SharePoint/Explorer navigation pane); the chevron alone toggles.
  const activate = (n: TreeNode) => {
    setFocusKey(n.key);
    if (n.href) {
      nav(n.href);
      if (isContainer(n)) toggle(n.key, true);
    } else if (isContainer(n)) toggle(n.key);
  };

  function onKeyDown(e: KeyboardEvent) {
    if (renaming) return;
    const i = visible.findIndex((v) => v.node.key === focusKey);
    const cur = visible[i];
    if (!cur) return;
    const n = cur.node;
    const go = (k: number) => {
      const target = visible[Math.max(0, Math.min(visible.length - 1, k))];
      if (target) setFocusKey(target.node.key);
    };
    switch (e.key) {
      case 'ArrowDown':
        go(i + 1);
        break;
      case 'ArrowUp':
        go(i - 1);
        break;
      case 'ArrowRight':
        if (n.children.length && !expanded.has(n.key)) toggle(n.key, true);
        else if (n.children.length) go(i + 1);
        break;
      case 'ArrowLeft':
        if (expanded.has(n.key)) toggle(n.key, false);
        else if (cur.parent) setFocusKey(cur.parent.key);
        break;
      case 'Home':
        go(0);
        break;
      case 'End':
        go(visible.length - 1);
        break;
      case 'Enter':
        activate(n);
        break;
      case 'F2':
        if (canRename(n)) setRenaming(n.key);
        break;
      case 'ContextMenu': {
        const el = treeRef.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(n.key)}"]`);
        const r = el?.getBoundingClientRect();
        setMenu({ node: n, x: (r?.left ?? 0) + 24, y: r?.bottom ?? 0 });
        break;
      }
      default:
        return;
    }
    e.preventDefault();
  }

  useEffect(() => {
    treeRef.current
      ?.querySelector<HTMLElement>(`[data-key="${CSS.escape(focusKey ?? '')}"]`)
      ?.focus({ preventScroll: false });
  }, [focusKey]);

  function canRename(n: TreeNode) {
    if (n.kind === 'folder') return commands.canManage;
    if (n.kind === 'sheet' || n.kind === 'doc') return n.isPrivate || commands.canManage;
    return false;
  }
  const canDrag = (n: TreeNode) =>
    (n.kind === 'folder' && commands.canManage) ||
    ((n.kind === 'sheet' || n.kind === 'doc') && (n.isPrivate ? commands.canPublish : commands.canManage));

  function onDragStart(e: DragEvent, n: TreeNode) {
    setDragKey(n.key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', n.label);
  }
  function dropAllowed(target: TreeNode) {
    if (!dragKey || !isDropTarget(target)) return false;
    if (target.key === dragKey) return false;
    if (dragKey.startsWith('folder:') && isDescendant(nodes, dragKey, target.key)) return false;
    return true;
  }
  function onDrop(e: DragEvent, target: TreeNode) {
    e.preventDefault();
    const dragged = visible.find((v) => v.node.key === dragKey)?.node ?? findNode(nodes, dragKey);
    setDragKey(null);
    setDropKey(null);
    if (dragged && dropAllowed(target)) commands.drop(dragged, folderIdOf(target));
  }

  return (
    <>
      <ul className="tree" role="tree" aria-label={t('tree.label')} ref={treeRef} onKeyDown={onKeyDown}>
        {visible.map(({ node: n, depth }) => {
          const open = expanded.has(n.key);
          const hasKids = n.children.length > 0 || n.kind === 'workspace';
          return (
            <li
              key={n.key}
              role="treeitem"
              aria-level={depth + 1}
              aria-expanded={isContainer(n) ? open : undefined}
              aria-selected={n.key === selectedKey}
              data-key={n.key}
              tabIndex={n.key === (focusKey ?? visible[0]?.node.key) ? 0 : -1}
              className={[
                'tree-item',
                `kind-${n.kind}`,
                n.key === selectedKey && 'selected',
                n.key === dropKey && 'drop-target',
                depth === 0 && 'top',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ paddingLeft: 6 + depth * 16 }}
              draggable={canDrag(n) && renaming !== n.key}
              onDragStart={(e) => onDragStart(e, n)}
              onDragEnd={() => (setDragKey(null), setDropKey(null))}
              onDragOver={(e) => {
                if (dropAllowed(n)) {
                  e.preventDefault();
                  setDropKey(n.key);
                }
              }}
              onDragLeave={() => setDropKey((k) => (k === n.key ? null : k))}
              onDrop={(e) => onDrop(e, n)}
              onClick={() => activate(n)}
              onDoubleClick={() => isContainer(n) && toggle(n.key)}
              onContextMenu={(e: MouseEvent) => {
                e.preventDefault();
                setFocusKey(n.key);
                setMenu({ node: n, x: e.clientX, y: e.clientY });
              }}
            >
              <span
                className="twisty"
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasKids) toggle(n.key);
                }}
              >
                {hasKids ? <Chevron open={open} /> : null}
              </span>
              <span className="tree-icon">
                {n.kind === 'doc' ? <FormatIcon format={n.format ?? 'native'} /> : ICON[n.kind](open)}
              </span>
              {renaming === n.key ? (
                <RenameInput
                  initial={n.label}
                  onDone={async (name) => {
                    setRenaming(null);
                    if (name && name !== n.label) await commands.rename(n, name);
                    setFocusKey(n.key);
                  }}
                />
              ) : (
                <span className="tree-label" title={n.label}>
                  {n.label}
                </span>
              )}
              {n.folderAccess && (
                <span
                  className={`tree-badge folder-${n.folderAccess}`}
                  title={t(n.folderAccess === 'locked' ? 'tree.folderLocked' : 'tree.folderRestricted')}
                >
                  <LockIcon />
                </span>
              )}
              {(n.isPrivate || n.eyesOnly) && (
                <span className="tree-badge" title={n.isPrivate ? t('tree.privateHint') : t('grid.eyesOnly')}>
                  <LockIcon />
                </span>
              )}
              {n.attention && (
                <span className="tree-badge" title={t('tree.connectedAttention')}>
                  <CloudIcon size={12} />!
                </span>
              )}
              {n.sub && <span className="tree-sub">{n.sub}</span>}
              {!!n.count && (
                <span className="count tree-count" aria-label={t('chat.unread', { count: n.count })}>
                  {n.count > 99 ? '99+' : n.count}
                </span>
              )}
              <button
                className="tree-more"
                aria-label={t('tree.more')}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  setMenu({ node: n, x: r.left, y: r.bottom });
                }}
              >
                ⋯
              </button>
            </li>
          );
        })}
      </ul>
      {menu && (
        <ContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)} items={menuItems(menu.node)} />
      )}
    </>
  );

  function menuItems(n: TreeNode): { label: string; run: () => void }[] {
    const items: { label: string; run: () => void }[] = [];
    if (n.href && n.kind !== 'root' && n.kind !== 'workspace')
      items.push({ label: t('tree.open'), run: () => activate(n) });
    if (n.kind === 'workspace')
      items.push({ label: t('tree.newPrivateSheet'), run: () => commands.newSheet(null, true) });
    if ((n.kind === 'folder' || n.kind === 'root') && commands.canManage) {
      items.push({
        label: t('tree.newFolder'),
        run: () => (toggle(n.key, true), commands.newFolder(folderIdOf(n))),
      });
      items.push({
        label: t('tree.newSheet'),
        run: () => (toggle(n.key, true), commands.newSheet(folderIdOf(n), false)),
      });
    }
    if (canRename(n)) items.push({ label: t('tree.rename'), run: () => setRenaming(n.key) });
    // #153 folder access (M18.4), where people look for it: who can see this folder, and changing it
    if (n.kind === 'folder' && n.href)
      items.push({ label: t('tree.whoCanSee'), run: () => nav(`${n.href}?access=1`) });
    if (
      (n.kind === 'folder' || ((n.kind === 'sheet' || n.kind === 'doc') && !n.isPrivate)) &&
      commands.canManage
    )
      items.push({ label: t('tree.moveTo'), run: () => commands.move(n) });
    if ((n.kind === 'sheet' || n.kind === 'doc') && n.isPrivate && commands.canPublish)
      items.push({ label: t('tree.publish'), run: () => commands.publish(n) });
    if (n.kind === 'root' && commands.canBrand)
      items.push({ label: t('brand.projectMenu'), run: () => commands.openBrand() });
    if (n.kind === 'root' && commands.canArchive)
      items.push(
        ...(commands.archived
          ? [
              { label: t('archive.menu.retention'), run: () => commands.archive('retention') },
              { label: t('archive.menu.unarchive'), run: () => commands.archive('unarchive') },
            ]
          : [{ label: t('archive.menu.archive'), run: () => commands.archive('archive') }]),
      );
    if (n.kind === 'governance' && commands.canManageBoards)
      items.push({ label: t('gov.newBoard'), run: () => commands.newBoard() });
    return items;
  }
}

function findNode(nodes: TreeNode[], key: string | null): TreeNode | undefined {
  if (!key) return undefined;
  return pathTo(nodes, key)?.at(-1);
}

function RenameInput({ initial, onDone }: { initial: string; onDone: (v: string | null) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const finish = (v: string | null) => {
    if (done.current) return;
    done.current = true;
    onDone(v?.trim() || null);
  };
  return (
    <input
      ref={ref}
      className="tree-rename"
      defaultValue={initial}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') finish(e.currentTarget.value);
        if (e.key === 'Escape') finish(null);
      }}
      onBlur={(e) => finish(e.currentTarget.value)}
    />
  );
}

export function ContextMenu({
  x,
  y,
  items,
  onClose,
}: {
  x: number;
  y: number;
  items: { label: string; run: () => void }[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLUListElement>(null);
  useEffect(() => {
    ref.current?.querySelector('button')?.focus();
    const close = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('blur', onClose);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);
  if (!items.length) {
    queueMicrotask(onClose);
    return null;
  }
  return (
    <ul
      ref={ref}
      className="context-menu"
      role="menu"
      style={{
        left: Math.min(x, window.innerWidth - 220),
        top: Math.min(y, window.innerHeight - items.length * 32 - 12),
      }}
      onKeyDown={(e) => {
        const btns = [...(ref.current?.querySelectorAll('button') ?? [])];
        const i = btns.indexOf(document.activeElement as HTMLButtonElement);
        if (e.key === 'Escape') onClose();
        if (e.key === 'ArrowDown') btns[(i + 1) % btns.length]?.focus();
        if (e.key === 'ArrowUp') btns[(i - 1 + btns.length) % btns.length]?.focus();
      }}
    >
      {items.map((it) => (
        <li key={it.label} role="none">
          <button
            role="menuitem"
            onClick={() => {
              onClose();
              it.run();
            }}
          >
            {it.label}
          </button>
        </li>
      ))}
    </ul>
  );
}
